<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 flex items-center justify-center z-[99999999999999] custom-lightbox"
    @keydown.esc="close"
    v-bind="$attrs"
    ref="lightbox"
    id="fullscreen-preview"
    tabindex="0"
  >
    <div
      class="overlay inset-0 bg-black bg-opacity-80 w-[100dvw] h-[100dvh] fixed"
      @click="close"
    ></div>
    <div
      class="relative z-50 flex items-center justify-center w-full h-full overflow-hidden rounded-lg"
    >
      <button
        class="absolute z-30 text-2xl font-bold text-white top-7.5 right-7.5 bg-black bg-opacity-10 rounded"
        @click="close"
      >
        <CloseIcon class="w-6 h-6 text-white" />
      </button>

      <button
        v-if="currentIndex > 0"
        class="absolute z-30 text-2xl text-white transform -translate-y-1/2 lg:left-8 left-5 top-1/2"
        @click.stop="prev"
        @keydown.left="prev"
      >
        <AngleLeftSwitchIcon
          class="w-10 h-10 md:w-[50px] md:h-[50px] rounded-full text-white bg-black/40 backdrop-blur-sm"
        />
      </button>
      <button
        v-if="currentIndex < items.length - 1"
        class="absolute z-30 text-2xl text-white transform -translate-y-1/2 lg:right-8 right-5 top-1/2"
        @click.stop="next"
        @keydown.right="next"
      >
        <AngleRightSwitchIcon
          class="w-10 h-10 md:w-[50px] md:h-[50px] text-white rounded-full bg-black/40 backdrop-blur-sm"
        />
      </button>

      <ImagePanZoom
        :item="item"
        :key="initialIndex"
        v-if="type === 'image' && appStore.isMobile"
      />
      <ImageZoom
        :item="item"
        :key="initialIndex"
        v-else-if="type === 'image'"
      />

      <template v-else-if="type === 'video'">
        <div
          class="relative rounded-[5px] overflow-hidden flex items-center justify-center !h-[100dvh] !w-[100dvw] p-5"
          v-if="!appStore.isMobile"
        >
          <MediaVideoPlayer
            v-if="loaded"
            :index="initialIndex"
            :key="initialIndex"
            :src="item.src"
            :poster="item.poster"
          />
        </div>

        <div
          v-else
          class="relative overflow-hidden flex items-center justify-center !h-[100dvh] !w-[100dvw]"
        >
          <MediaVideoPlayerMobile
            v-if="loaded"
            :index="initialIndex"
            :key="initialIndex"
            :src="item.src"
            :poster="item.poster"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import CloseIcon from "~/assets/svg/heroicons/close.svg?component";
import { useAppStore } from "~/store/app";
import AngleLeftSwitchIcon from "~/assets/svg/heroicons/angle-left-switch.svg?component";
import AngleRightSwitchIcon from "~/assets/svg/heroicons/angle-right-switch.svg?component";
import SearchIcon from "~/assets/svg/heroicons/search.svg?component";

const props = defineProps({
  items: Array,
  initialIndex: Number,
});

const appStore = useAppStore();
const emit = defineEmits(["update:visible"]);

const zoomer = ref();
const isVideoLoad = ref(false);
const controller = ref(null);
const lightbox = ref(null);
const loaded = ref(true);
const currentIndex = ref(props.initialIndex);
const state = reactive({});

const type = computed(() => {
  if (props.items[currentIndex.value]) {
    if (props.items[currentIndex.value].type.includes("video")) {
      return "video";
    }
  }
  return "image";
});
const item = computed(() => {
  return props.items[currentIndex.value];
});

const isVisible = computed(() => {
  return currentIndex.value !== null;
});

watch(
  props,
  (newValue, oldValue) => {
    if (newValue.initialIndex != currentIndex.value) {
      currentIndex.value = newValue.initialIndex;
    }
  },
  { deep: true }
);

watch(
  lightbox,
  (newValue, oldValue) => {
    if (lightbox.value) {
      lightbox.value.focus();
      document.onkeydown = checkKey;
    }
  },
  { deep: true, flush: "post" }
);

onMounted(() => {
  if (lightbox.value) {
    lightbox.value.focus();
  }
  if (import.meta.browser) {
    document.onkeydown = checkKey;
  }
});

onBeforeUnmount(() => {
  emit("update:visible", null);

  setTimeout(() => {
    const containerEl = document.getElementById("gallery");
    if (containerEl) {
      containerEl.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, 5);
  document.onkeydown = null;
});

function checkKey(e) {
  e = e || window.event;
  if (e.keyCode == "37") {
    prev();
  } else if (e.keyCode == "39") {
    next();
  }
}

function closeOnKey(event) {
  if (event.key === "Escape") {
    close();
  }
}

function close() {
  currentIndex.value = null;
  emit("update:visible", null);
  document.onkeydown = null;
}

function prev() {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    loaded.value = false;
    setTimeout(() => {
      loaded.value = true;
    }, 20);
  }
}

function next() {
  if (currentIndex.value < props.items.length - 1) {
    currentIndex.value++;
    loaded.value = false;
    setTimeout(() => {
      loaded.value = true;
    }, 20);
  }
}
</script>

<style scoped>
/* Add scoped styles if needed */
.custom-lightbox video {
  @apply !h-[100dvh] !w-[100dvw] md:!h-[95dvh]  md:!w-[97dvw] object-cover rounded-[5px] bg-black bg-opacity-70 backdrop-blur-[23px];
}
.custom-lightbox .video-poster {
  @apply !h-[100dvh] !w-[100dvw] md:!h-[95dvh]  md:!w-[97dvw] object-cover rounded-[5px];
}
.video-controls {
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0px 0px 2px 2px;
  backdrop-filter: blur(12.5px);
  @apply gap-2  lg:gap-6 px-2 md:px-5 h-10 w-full justify-between items-center absolute bottom-0 rounded-b-[5px] z-[999999999999];
}
.video-controls[data-state="hidden"] {
  display: none;
}

.video-controls[data-state="visible"] {
  display: block;
}
.video-controls > * {
  @apply block h-5;
}

.video-controls > *:first-child {
  margin-left: 0;
}
.video-controls .progress {
  @apply cursor-pointer w-[75%] md:w-full rounded-full h-2;
}
.video-controls button {
  border: none;
  cursor: pointer;
  background: transparent;
  background-size: contain;
  background-repeat: no-repeat;
  @apply md:!min-w-[20px] md:h-5 h-4 w-4;
}
.video-controls button:hover,
.video-controls button:focus {
  opacity: 0.5;
}
#remaining-time {
  min-width: 47px;
}
.video-controls progress {
  /* -moz-border-radius: 100%; */
  /* -webkit-border-radius: 100%; */
  @apply rounded-full overflow-hidden text-[#0095dd] border-none w-full h-full block relative;
}
.video-controls progress[data-state="fake"] {
  background: #e6e6e6;
  height: 65%;
}
.video-controls progress span {
  width: 0%;
  height: 100%;
  display: inline-block;
  background-color: #2a84cd;
}
.video-controls #progress-dot {
  width: 20px;
  height: 20px;
  left: 0;
  position: absolute;
  top: -6px;
  z-index: 9999999999999;
  background: white;
  border-radius: 100%;
}
.video-controls progress::-moz-progress-bar {
  background-color: #0095dd;
}

.video-controls progress::-webkit-progress-value {
  background-color: #0095dd;
}
figure[data-fullscreen="true"] video::-webkit-media-controls {
  display: none !important;
}
figure[data-fullscreen="true"] .controls {
  z-index: 2147483647;
}
</style>
